import React, { useState, useEffect } from 'react';

const AlumniSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [alumniImages, setAlumniImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch alumni images from API
  useEffect(() => {
    const fetchAlumniImages = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://admin.pakschoolandcollege.com/api/alumni.php');
        const data = await response.json();
        
        if (data.success) {
          setAlumniImages(data.data);
        } else {
          setError('Failed to load alumni images');
        }
      } catch (err) {
        console.error('Error fetching alumni images:', err);
        setError('Failed to load alumni images');
      } finally {
        setLoading(false);
      }
    };

    fetchAlumniImages();
  }, []);

  const slidesToShow = 6; // Number of images to show at once
  const totalSlides = Math.ceil(alumniImages.length / slidesToShow);

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const openModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
    setIsAutoPlaying(false); // Pause auto-sliding when modal is open
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    setIsAutoPlaying(true); // Resume auto-sliding when modal is closed
  };

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        closeModal();
      }
    };

    if (isModalOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  // Show loading state
  if (loading) {
    return (
      <section className="py-6 sm:py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Alumni</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-red-500 mx-auto mb-3 sm:mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Celebrating the achievements of our distinguished alumni who continue to make us proud
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Loading alumni images...</span>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="py-6 sm:py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Alumni</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-red-500 mx-auto mb-3 sm:mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Celebrating the achievements of our distinguished alumni who continue to make us proud
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="text-red-500 text-lg mb-2">⚠️</div>
              <p className="text-gray-600">{error}</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show empty state if no images
  if (alumniImages.length === 0) {
    return (
      <section className="py-6 sm:py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="text-center mb-6 sm:mb-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Alumni</h2>
            <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-red-500 mx-auto mb-3 sm:mb-4"></div>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
              Celebrating the achievements of our distinguished alumni who continue to make us proud
            </p>
          </div>
          <div className="flex justify-center items-center py-12">
            <div className="text-center">
              <div className="text-gray-400 text-lg mb-2">📸</div>
              <p className="text-gray-600">No alumni images available at the moment.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-6 sm:py-8 md:py-12 bg-white">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 mb-3 sm:mb-4">Our Alumni</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-red-500 mx-auto mb-3 sm:mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto">
            Celebrating the achievements of our distinguished alumni who continue to make us proud
          </p>
        </div>
        {/* Simple Slider Container */}
        <div className="relative">
          {/* Navigation Arrows - Hidden on mobile, visible on larger screens */}
          <button
            onClick={prevSlide}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
            className="hidden sm:flex absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 bg-white/90 hover:bg-white shadow-lg rounded-full items-center justify-center transition-all duration-300 hover:scale-110"
            aria-label="Previous slide"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
            className="hidden sm:flex absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 bg-white/90 hover:bg-white shadow-lg rounded-full items-center justify-center transition-all duration-300 hover:scale-110"
            aria-label="Next slide"
          >
            <svg className="w-4 h-4 md:w-5 md:h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Images Container */}
          <div className="overflow-hidden rounded-lg sm:rounded-xl">
            <div 
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentSlide * 100}%)` }}
            >
              {Array.from({ length: totalSlides }).map((_, slideIndex) => (
                <div key={slideIndex} className="w-full flex-shrink-0">
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2 sm:gap-3 md:gap-4 p-1 sm:p-2">
                    {alumniImages.slice(slideIndex * slidesToShow, (slideIndex + 1) * slidesToShow).map((alumni, index) => (
                      <div
                        key={`${slideIndex}-${index}`}
                        className="group relative overflow-hidden rounded-md sm:rounded-lg shadow-sm sm:shadow-md hover:shadow-lg sm:hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer"
                        onClick={() => openModal(alumni)}
                      >
                        <img
                          src={`https://admin.pakschoolandcollege.com/${alumni.image_path}`}
                          alt={alumni.title || `Alumni ${slideIndex * slidesToShow + index + 1}`}
                          className="w-full h-28 sm:h-36 md:h-44 lg:h-52 object-contain bg-gray-100 transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        {/* Click overlay with icon - smaller on mobile */}
                        <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <div className="bg-white/90 rounded-full p-1 sm:p-2 transform scale-75 group-hover:scale-100 transition-transform duration-300">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Navigation Buttons */}
          <div className="sm:hidden flex justify-center mt-4 space-x-4">
            <button
              onClick={prevSlide}
              className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md"
              aria-label="Previous slide"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              onClick={nextSlide}
              className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-full flex items-center justify-center transition-all duration-300 shadow-md"
              aria-label="Next slide"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-3 sm:mt-4 space-x-1 sm:space-x-2">
            {Array.from({ length: totalSlides }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                onMouseEnter={() => setIsAutoPlaying(false)}
                onMouseLeave={() => setIsAutoPlaying(true)}
                className={`w-2 h-2 sm:w-2.5 sm:h-2.5 md:w-3 md:h-3 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-blue-600 scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Best View Modal */}
      {isModalOpen && selectedImage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-2 sm:p-4">
          <div className="relative w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh]">
            {/* Close Button */}
            <button
              onClick={closeModal}
              className="absolute -top-8 sm:-top-12 right-0 w-8 h-8 sm:w-10 sm:h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
              aria-label="Close modal"
            >
              <svg className="w-4 h-4 sm:w-6 sm:h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Image */}
            <div className="bg-white rounded-lg sm:rounded-xl overflow-hidden shadow-2xl">
              <img
                src={`https://admin.pakschoolandcollege.com/${selectedImage.image_path}`}
                alt={selectedImage.title || "Alumni Best View"}
                className="w-full h-auto max-h-[75vh] sm:max-h-[80vh] object-contain"
              />
              
              {/* Image Info */}
              <div className="p-3 sm:p-4 bg-white border-t">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">{selectedImage.title || "Alumni Photo"}</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Tap outside or press ESC to close</p>
                  </div>
                  <button
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = `https://admin.pakschoolandcollege.com/${selectedImage.image_path}`;
                      link.download = selectedImage.title || 'alumni-image';
                      link.click();
                    }}
                    className="w-full sm:w-auto px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm rounded-lg transition-colors duration-300"
                  >
                    Download
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Backdrop Click to Close */}
          <div
            className="absolute inset-0 -z-10"
            onClick={closeModal}
          ></div>
        </div>
      )}
    </section>
  );
};

export default AlumniSection;
